import {
  currentCheckinTime,
  generateSecurityCode,
  type CheckinTimeRule,
  type LocationGender,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { describeError } from '../errors.ts'
import { useCheckinQueue, type QueuedCheckin } from '../offline/context.tsx'
import { randomUUIDv7 } from '../offline/id.ts'
import { PermanentSendError, type QueuedItem } from '../offline/queue.ts'

/**
 * Check-in, including the part where the network is gone.
 *
 * The acceptance criterion is "kill the network mid-service: check-in
 * continues, labels print, all records sync on reconnect with zero duplicates
 * and zero losses". Everything here is shaped by that sentence.
 *
 * A check-in is **written to the queue first and sent second**. The id and the
 * security code are both generated on the device, so the label is complete and
 * printable before anything has left the building — and because the id is the
 * server's idempotency key, a send that is retried after a lost response is a
 * no-op rather than a second child.
 */

export const checkinKeys = {
  all: () => ['checkin'] as const,
  locations: (campusId?: string) => [...checkinKeys.all(), 'locations', campusId ?? null] as const,
  search: (query: string, timeId: string | null = null) =>
    [...checkinKeys.all(), 'search', query, timeId] as const,
  times: () => [...checkinKeys.all(), 'times'] as const,
  currentTime: () => [...checkinKeys.all(), 'times', 'current'] as const,
  roster: (locationId: string, includeCheckedOut: boolean) =>
    [...checkinKeys.all(), 'roster', locationId, includeCheckedOut] as const,
}

export function useCheckinLocations(campusId?: string) {
  const api = useApi()
  return useQuery({
    queryKey: checkinKeys.locations(campusId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.locations.get({
        query: campusId ? { campusId } : {},
      })
      if (error) throw error
      return data
    },
    // A room's count changes every few seconds during a service.
    refetchInterval: 10_000,
  })
}

export type CheckinLocation = NonNullable<ReturnType<typeof useCheckinLocations>['data']>[number]

/**
 * A family, from a surname or a phone number.
 *
 * Not debounced here — the kiosk submits the search explicitly, because a
 * volunteer types a phone number and a per-keystroke query on a foyer wifi is
 * six wasted round trips before the useful one.
 */
export function useHouseholdSearch(query: string, enabled: boolean, timeId: string | null = null) {
  const api = useApi()
  return useQuery({
    queryKey: checkinKeys.search(query, timeId),
    enabled: enabled && query.trim().length >= 2,
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.search.get({
        query: { q: query, ...(timeId ? { timeId } : {}) },
      })
      if (error) throw error
      return data
    },
    // Always asked again when online: who is already checked in changes by
    // the second on a Sunday, and a cached "not here yet" offers a room to a
    // child who is in one. Offline the query pauses and the cached answer is
    // all there is — the sync then refuses a duplicate, and says so.
    staleTime: 0,
  })
}

export type SearchHousehold = NonNullable<ReturnType<typeof useHouseholdSearch>['data']>[number]
export type SearchPerson = SearchHousehold['members'][number]

export function useRoster(locationId: string, includeCheckedOut = false) {
  const api = useApi()
  return useQuery({
    queryKey: checkinKeys.roster(locationId, includeCheckedOut),
    enabled: locationId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin
        .locations({ id: locationId })
        .roster.get({ query: { includeCheckedOut } })
      if (error) throw error
      return data
    },
    refetchInterval: 10_000,
  })
}

export type RosterEntry = NonNullable<ReturnType<typeof useRoster>['data']>[number]

/** What the station needs to print, whether or not the server has heard yet. */
export interface PendingLabel {
  clientId: string
  personId: string
  personName: string
  locationId: string
  locationName: string
  securityCode: string
  labelNotes: string | null
  checkedInAt: string
  /** false until the server has acknowledged it. */
  synced: boolean
  /** The server refused it (see `RefusedCheckin`): never recorded. */
  refused?: boolean
  /** A volunteer's badge, not a child's label: no pickup code is needed. */
  serving?: boolean
}

/**
 * A check-in taken on this device that the server would not record — another
 * kiosk had already checked the child in, or the room filled while this one
 * was offline. The label has printed, so a volunteer must sort it out.
 */
export interface RefusedCheckin {
  clientId: string
  personName: string
  locationName: string
  securityCode: string
  reason: string
}

/** The code the label carries, drawn from the platform CSPRNG. */
function newSecurityCode(): string {
  return generateSecurityCode((bytes) => crypto.getRandomValues(bytes))
}

/**
 * Takes a check-in.
 *
 * Returns the label immediately, from the values the device generated. The
 * send happens after, and its failure is not the caller's problem: the item is
 * in the queue and the drain will get to it.
 */
export function useStationCheckin(stationId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const queue = useCheckinQueue()
  const [pending, setPending] = useState<PendingLabel[]>([])

  if (!queue) {
    // Not a fallback to posting directly, as attendance does. A kiosk without
    // a durable queue is a kiosk that loses children on a reload, and failing
    // loudly at mount is much better than discovering that at 11am.
    throw new Error('Wrap the station in <CheckinQueueProvider> before taking check-ins.')
  }

  const send = useCallback(
    async (item: QueuedItem<QueuedCheckin>) => {
      const { label, ...body } = item.payload
      void label
      const { error } = await api.api.v1.checkin.post(body)
      if (error) {
        // A 4xx will fail identically forever — a room that was deleted, a
        // person who was merged away. Dropping it is better than retrying
        // until the end of time, and the row is still in the audit trail of
        // whatever rejected it.
        //
        // A 409 is permanent too: "already checked in" (another kiosk got
        // there first) or "the room is full" will not change by retrying, and
        // the label has already printed — so it is set aside for a volunteer,
        // with the server's reason, rather than retried in silence.
        const status = (error as { status?: number }).status
        if (
          status !== undefined &&
          status >= 400 &&
          status < 500 &&
          status !== 408 &&
          status !== 429
        ) {
          throw new PermanentSendError(describeError(error))
        }
        throw new Error('check-in not sent')
      }
    },
    [api],
  )

  const refreshPending = useCallback(async () => {
    const items = await queue.list()
    const rejected = await queue.rejected()
    setPending((current) =>
      current.map((entry) => {
        const refused = rejected.some((item) => item.id === entry.clientId)
        return {
          ...entry,
          refused,
          synced: !refused && !items.some((item) => item.id === entry.clientId),
        }
      }),
    )
  }, [queue])

  const drain = useCallback(async () => {
    const result = await queue.flush(send)
    await refreshPending()
    if (result.sent > 0) {
      void queryClient.invalidateQueries({ queryKey: checkinKeys.all() })
    }
    return result
  }, [queryClient, queue, refreshPending, send])

  const checkIn = useMutation({
    /**
     * `always`, and this is load-bearing.
     *
     * TanStack Query's default `networkMode: 'online'` *pauses* a mutation
     * while the browser reports itself offline, and resumes it on reconnect.
     * That is right for a mutation that is an HTTP request. This one is not:
     * its job is to write to IndexedDB and hand back a printable label, and
     * pausing it means the volunteer taps a room and nothing happens — no
     * label, no queue entry, no error — until the wifi returns.
     *
     * Which is precisely the failure the offline design exists to prevent, and
     * it is invisible to every test that does not actually pull the cable.
     */
    networkMode: 'always',
    mutationFn: async (input: {
      person: { personId: string; firstName: string; lastName: string; labelNotes: string | null }
      location: { id: string; name: string }
      /** The service being checked in to (the station's current one). */
      timeId?: string | null
      /** Checking an adult in to serve rather than to attend. */
      serving?: boolean
    }): Promise<PendingLabel> => {
      const clientId = randomUUIDv7()
      const securityCode = newSecurityCode()
      const checkedInAt = new Date().toISOString()

      const payload: QueuedCheckin = {
        clientId,
        stationId,
        personId: input.person.personId,
        locationId: input.location.id,
        securityCode,
        clientCheckedInAt: checkedInAt,
        method: 'station',
        ...(input.timeId ? { checkinTimeId: input.timeId } : {}),
        ...(input.serving ? { kind: 'volunteer' as const } : {}),
        label: {
          personName: `${input.person.firstName} ${input.person.lastName}`,
          locationName: input.location.name,
          labelNotes: input.serving ? null : input.person.labelNotes,
        },
      }

      // Storage first, network second. If the tab dies between these two
      // lines the child is still recorded; if it died in the other order they
      // would not be.
      await queue.enqueue(clientId, payload)

      const label: PendingLabel = {
        clientId,
        personId: input.person.personId,
        personName: payload.label.personName,
        locationId: input.location.id,
        locationName: input.location.name,
        securityCode,
        labelNotes: input.serving ? null : input.person.labelNotes,
        checkedInAt,
        synced: false,
        serving: input.serving === true,
      }
      setPending((current) => [label, ...current])

      // Best effort, right now. Failure is expected and handled by the drain.
      void drain()
      return label
    },
  })

  // Drain on reconnect, and on a timer for the case where `online` never fires
  // because the wifi came back without the browser noticing.
  useEffect(() => {
    void refreshPending()
    const onOnline = () => void drain()
    globalThis.addEventListener?.('online', onOnline)
    const timer = setInterval(() => void drain(), 15_000)
    return () => {
      globalThis.removeEventListener?.('online', onOnline)
      clearInterval(timer)
    }
  }, [drain, refreshPending])

  const [queued, setQueued] = useState(0)
  const [refused, setRefused] = useState<RefusedCheckin[]>([])
  useEffect(() => {
    let alive = true
    const tick = async () => {
      const items = await queue.list()
      const rejected = await queue.rejected()
      if (!alive) return
      setQueued(items.length)
      setRefused((current) =>
        current.length === rejected.length &&
        current.every((entry, i) => entry.clientId === rejected[i]?.id)
          ? current
          : rejected.map((item) => ({
              clientId: item.id,
              personName: item.payload.label.personName,
              locationName: item.payload.label.locationName,
              securityCode: item.payload.securityCode,
              reason: item.lastError ?? 'The server refused this check-in.',
            })),
      )
    }
    void tick()
    const timer = setInterval(() => void tick(), 2_000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [queue])

  /** A volunteer has dealt with a refused check-in. */
  const dismiss = async (clientId: string) => {
    await queue.dismiss(clientId)
    setRefused((current) => current.filter((entry) => entry.clientId !== clientId))
  }

  return { checkIn, pending, queued, refused, dismiss, drain, clearPending: () => setPending([]) }
}

export function useCheckout() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      checkinId,
      ...body
    }: {
      checkinId: string
      securityCode?: string | null
      collectorPersonId?: string | null
      collectorName?: string | null
      supervisorOverride?: boolean
      note?: string | null
    }) => {
      const { data, error } = await api.api.v1.checkin({ id: checkinId }).checkout.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}

export function useCheckinStations() {
  const api = useApi()
  return useQuery({
    queryKey: [...checkinKeys.all(), 'stations'],
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.stations.get()
      if (error) throw error
      return data
    },
  })
}

export function useRegisterStation() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; mode?: 'self' | 'assisted' }) => {
      const { data, error } = await api.api.v1.checkin.stations.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}

/** What a room is set up with, beyond its name. */
export interface RoomRules {
  description?: string | null
  servesChildren?: boolean
  minAgeMonths?: number | null
  maxAgeMonths?: number | null
  minGrade?: number | null
  maxGrade?: number | null
  capacity?: number | null
  gender?: LocationGender | null
  ageAsOf?: string | null
  timeIds?: string[]
  childrenPerVolunteer?: number | null
}

export function useCreateCheckinLocation() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: RoomRules & { name: string }) => {
      const { data, error } = await api.api.v1.checkin.locations.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}

export function useUpdateCheckinLocation() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: RoomRules & { id: string; name?: string; isActive?: boolean }) => {
      const { data, error } = await api.api.v1.checkin.locations({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}

/** Refused (409) while a child is checked in to the room. */
export function useRemoveCheckinLocation() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.checkin.locations({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}

/** Whether the browser thinks it is online — the kiosk's status light. */
// `packages/data` has no DOM lib on purpose, and React Native has no
// `navigator.onLine` at all — so this reads it structurally and defaults to
// online, which is right on mobile (NetInfo answers there instead).
const browserOnline = (): boolean =>
  (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine ?? true

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(browserOnline)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    const update = () => {
      if (mounted.current) setOnline(browserOnline())
    }
    globalThis.addEventListener?.('online', update)
    globalThis.addEventListener?.('offline', update)
    return () => {
      mounted.current = false
      globalThis.removeEventListener?.('online', update)
      globalThis.removeEventListener?.('offline', update)
    }
  }, [])
  return online
}

// ---- service times -----------------------------------------------------------

/** The weekly services check-in runs for (Planning Center's event times). */
export function useCheckinTimes(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: checkinKeys.times(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.times.get()
      if (error) throw error
      return data
    },
  })
}

export type CheckinTime = NonNullable<ReturnType<typeof useCheckinTimes>['data']>[number]

export interface CheckinTimeBody {
  name: string
  campusId?: string | null
  dayOfWeek: number
  startsAt: string
  endsAt: string
  opensMinutesBefore?: number
}

export function useCheckinTimeMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  // Times reshape the rooms (a removed time frees its rooms) and the search.
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() })
  const create = useMutation({
    mutationFn: async (body: CheckinTimeBody) => {
      const { data, error } = await api.api.v1.checkin.times.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: async ({ id, ...body }: Partial<CheckinTimeBody> & { id: string }) => {
      const { data, error } = await api.api.v1.checkin.times({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.checkin.times({ id }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
  return { create, update, remove }
}

/**
 * The service a station is checking in to.
 *
 * The server says which one is running and in which time zone; the station
 * then keeps working it out from its own clock every half-minute, so a
 * station that loses the network at 10:25 still moves from the 9:00 to the
 * 11:00 on time. A volunteer can pick another service by hand (a late family
 * for the early service); `null` goes back to automatic.
 */
export function useCurrentService(campusId: string | null = null) {
  const api = useApi()
  const query = useQuery({
    queryKey: checkinKeys.currentTime(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.times.current.get({
        query: campusId ? { campusId } : {},
      })
      if (error) throw error
      return data
    },
    refetchInterval: 5 * 60_000,
  })
  const [now, setNow] = useState(() => new Date())
  const [chosenId, setChosenId] = useState<string | null>(null)
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const times: CheckinTimeRule[] = query.data?.times ?? []
  const automatic = query.data
    ? currentCheckinTime(times, now, query.data.timeZone, campusId)
    : null
  const chosen = chosenId ? (times.find((time) => time.id === chosenId) ?? null) : null
  return {
    /** The service in use: chosen by hand, or the one running now; null ⇒ every room. */
    service: chosen ?? automatic,
    automatic,
    times,
    chosenId,
    choose: setChosenId,
    isPending: query.isPending,
  }
}
